// Every running screen/flow experiment, in one place.
//
// An experiment maps a CleverTap flag to the screen each of its variants
// renders; <ExperimentRoute experiment={...} /> in App.tsx does the rest.
// Starting, stopping, splitting or shipping a variant to everyone is a
// dashboard change. Removing an experiment (once a variant has won) is: point
// the route at the winner, delete the loser and the entry here.
//
// Rule: one experiment per screen at a time, or their results mix.

import InstaLoader from 'components/LoaderComponent/InstaLoader';
import PlanPageLoader from 'components/PlanPageLoader/PlanPageLoader';
import type { FeatureFlag } from 'infrastructure/serviceHandlers/Analytics/featureFlags';
import { FEATURE_FLAG } from 'infrastructure/serviceHandlers/Analytics/featureFlags';
import type { ComponentType, ReactElement } from 'react';
import {
  usePlanSelectionEligible,
  usePromotionEligible
} from './useHomeEligibility';

/** A screen's dynamic import; ExperimentRoute preloads it and lazy-renders it. */
export type ScreenLoader = () => Promise<{ default: ComponentType }>;

export type Experiment = {
  /** Id in analytics, the session lock and the QA override (`?exp=<key>:<variant>`). */
  key: string;
  flag: FeatureFlag;
  /** Variant key -> the screen's import. Must include `control`. */
  variants: Record<string, ScreenLoader>;
  /** Shown while the variant is being decided. */
  loader: ReactElement;
  /**
   * Hook: whether this user is in the experiment at all — false gets control
   * with no exposure, undefined waits. Omit when everyone is eligible.
   */
  useEligible?: () => boolean | undefined;
};

export const EXPERIMENTS = {
  // Live: the promotion page after camera naming (first-time pairing).
  PROMOTION: {
    key: 'promotion',
    flag: FEATURE_FLAG.PROMOTION_SCREEN,
    variants: {
      control: () => import('pages/v1/HomeUpsellV1'),
      v2: () => import('pages/v2/Promotion')
    },
    loader: <InstaLoader />,
    useEligible: usePromotionEligible
  },

  // Built but not routed: the plan list is back on its current screen while
  // the promotion test runs. To restart it, route ROUTES.HOME_BUY_SUBSCRIPTION
  // through <ExperimentRoute experiment={EXPERIMENTS.PLAN_SELECTION} /> in
  // App.tsx again.
  PLAN_SELECTION: {
    key: 'plan_selection',
    flag: FEATURE_FLAG.PLAN_SELECTION_SCREEN,
    variants: {
      control: () => import('pages/v1/HomeBuySubscriptionV1'),
      v2: () => import('pages/v2/PlanSelection')
    },
    loader: <PlanPageLoader canBuyPlan />,
    useEligible: usePlanSelectionEligible
  }
} satisfies Record<string, Experiment>;
